/* CnbFakta — co o kandidátovi VÍME z veřejného registru ČNB.
 *
 * PROČ EXISTUJE: náborový radar při importu ukládal do recruit_meta obec, kraj, IČO,
 * kategorie registrace i datum vzniku vazby — a pak to nikde neukázal. Poradce viděl
 * prázdnou poznámku a musel si všechno hledat znovu. Tenhle blok tvrdá data zobrazí
 * a jasně oddělí je od toho, co si poradce zjistil sám.
 *
 * PRAVIDLO: zobrazuje se JEN to, co v datech opravdu je. Nic se nedopočítává „aby to
 * vypadalo plně" — prázdné pole se prostě neukáže.
 */
import { Landmark, MapPin, Building2, Clock, ShieldCheck, Info } from "lucide-react";
import { pluralJazyka } from "@/lib/i18n/textMimoReact";
import { registrKandidata } from "@/lib/naborCl14";
import { TL } from "@/lib/prekladyUI";

/* Rok/roky ve správném tvaru. null → nic (netvrdíme, co nevíme).
 *
 * Tvar podle počtu dělá `pluralJazyka` (Intl.PluralRules nad slovníkem), ne
 * ruční pravidlo 1 / 2–4 / 5+. To je pravidlo ČEŠTINY — Ukrajinec by z něj
 * dostal „21 років" místo správného „21 рік" a Vietnamec rovnou češtinu.
 * ⚠️ „méně než rok" zůstává ternárem schválně: pro tenhle text nemají slovníky
 * klíč a vymýšlet uk/vi překlad není moje práce (viz seznam chybějících). */
export function rokyText(roky, L = "cs") {
  if (roky == null || roky === "") return null;
  const r = Math.round(Number(roky) * 10) / 10;
  if (!isFinite(r)) return null;
  const cely = Math.round(r);
  if (r < 1) return L === "sk" ? "menej než rok" : "méně než rok";
  return `${cely} ${pluralJazyka(L, "nastroje.pdVety.rok", cely)}`;
}

export default function CnbFakta({ meta = {}, L = "cs", compact = false }) {
  const m = meta || {};
  const mistoParts = [m.obec, m.kraj && m.kraj !== m.obec ? m.kraj : null].filter(Boolean);
  const roky = rokyText(m.roky, L);
  /* Registr podle toho, ODKUD kandidát přišel (recruit_meta.zeme), ne podle
     jazyka: kandidát z NBS je z NBS i v češtině (18. 10. 2026, DTN-9). */
  const registr = registrKandidata(m);
  /* Ručně založený kandidát (jen firma, bez stopy importu) z registru NEPŘIŠEL —
     nadpis „z registru ČNB“ by u něj lhal (u SK poradce dvakrát). Import radaru
     vždy ukládá zemi a zdroj (CnbPoradciPage), starší import aspoň IČO/kategorie. */
  const zRegistru = !!(m.zeme || m.zdroj || m.ico || m.kategorie || m.vznik || m.roky);
  const radky = [
    m.firma ? { id: "firma", icon: Building2, label: L === "sk" ? "Robí u" : "Dělá u", value: m.firma } : null,
    mistoParts.length ? { id: "misto", icon: MapPin, label: L === "sk" ? "Pôsobí" : "Působí", value: mistoParts.join(" · ") } : null,
    roky || m.vznik
      ? {
        id: "praxe", icon: Clock, label: L === "sk" ? "V obore" : "V oboru",
        value: [roky, m.vznik ? (L === "sk" ? `od ${m.vznik}` : `od ${m.vznik}`) : null].filter(Boolean).join(" · "),
      }
      : null,
    m.kategorie ? { id: "kat", icon: ShieldCheck, label: L === "sk" ? "Registrovaný na" : "Registrován na", value: m.kategorie } : null,
    m.ico ? { id: "ico", icon: Landmark, label: "IČO", value: m.ico } : null,
  ].filter(Boolean);

  if (radky.length === 0) {
    return (
      <div className="rounded-xl px-3 py-2.5 text-[11.5px] leading-snug" style={{ background: "var(--bg)", border: "1px dashed var(--line)", color: "var(--muted)" }} data-testid="nabor-cnb-prazdno">
        {L === "sk"
          ? "Tento kandidát neprišiel z náborového radaru, takže o ňom nemáme žiadne overené údaje. Doplň si ich nižšie ručne."
          : "Tenhle kandidát nepřišel z náborového radaru, takže o něm nemáme žádné ověřené údaje. Doplň si je níž ručně."}
      </div>
    );
  }

  return (
    <div className="rounded-xl px-3 py-2.5" style={{ background: "var(--bg)", border: "1px solid var(--line)" }} data-testid="nabor-cnb-fakta">
      <div className="flex items-center gap-1.5 mb-1.5">
        <Landmark size={12} style={{ color: "var(--accent)" }} />
        <span className="text-[10px] uppercase tracking-[0.14em] font-bold" style={{ color: "var(--accent)" }}>
          {zRegistru
            ? (L === "sk" ? `Čo vieme z registra ${registr}` : `Co víme z registru ${registr}`)
            : TL("Co o něm víme", L)}
        </span>
      </div>
      <div className="space-y-1">
        {radky.map((r) => {
          const Icon = r.icon;
          return (
            <div key={r.id} className="flex items-start gap-1.5 text-[11.5px] leading-snug" data-testid={`nabor-cnb-${r.id}`}>
              <Icon size={11} style={{ color: "var(--faint, #746F64)", flexShrink: 0, marginTop: 2 }} />
              <span style={{ color: "var(--muted)", minWidth: compact ? 0 : 92, flexShrink: 0 }}>{r.label}</span>
              <span className="font-semibold" style={{ color: "var(--ink)" }}>{r.value}</span>
            </div>
          );
        })}
      </div>
      {zRegistru && <div className="flex items-start gap-1.5 mt-2 text-[10.5px] leading-snug" style={{ color: "var(--muted)" }}>
        <Info size={10} style={{ flexShrink: 0, marginTop: 2 }} />
        <span>
          {L === "sk"
            ? "Register neuvádza telefón ani e-mail — kontakt si dohľadaj ručne. Ostatné si over, dáta môžu byť staršie."
            : "Registr neuvádí telefon ani e-mail — kontakt si dohledej ručně. Zbytek si ověř, data můžou být starší."}
        </span>
      </div>}
    </div>
  );
}
